import { HOST_API } from 'src/config-global';

import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

// ----------------------------------------------------------------------
const axiosInstance = axios.create({ baseURL: HOST_API });

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    login: '/auth/login',
    getLoginOptions: '/auth/getLoginOptions',
    register: '/auth/register', //register email only
    connectWithToken: (token: string) => `/auth/connect/${token}`,
    setupAccount: '/auth/setupAccount', //finalize registration
    refreshToken: '/auth/refreshToken',
    revokeRefreshToken: '/auth/revokeRefreshToken',
    checkUserToken: '/auth/checkUserToken',
    sendEmailToReset: '/auth/submitEmailForPasswordReset',
    verifyPasswordTokenValidity: '/auth/verifyPasswordTokenValidity',
    updatePasswordWithToken: '/auth/updatePasswordWithToken',
    googleLogin: '/auth/google',
    googleDisconnect: '/auth/google/disconnect',
    appleLogin: '/auth/apple',
  },
  users: {
    root: (id?: number) => `/user/${id || ''}`,
    available: '/user/available',
    filteredList: (filter: string) => `/user?filter=${filter}`,
    me: {
      root: '/user/me',
      documents: '/user/me/documents',
      documentDownload: (documentId: number) =>
        `/user/me/documents/${documentId}/download`,
      language: '/user/me/language',
      timezone: '/user/me/timezone',
      changePassword: '/user/me/changePassword',
      deleteAccount: '/user/me/deleteAccount',
      avatar: '/user/me/avatar',
    },
    deactivate: (id: number) => `/user/deactivate/${id}`,
    deactivateGroup: '/user/deactivate-group',
    reactivate: (id: number) => `/user/reactivate/${id}`,
    invite: (id: number) => `/user/${id}/invite`,
    folderAccess: (id: number) => `/user/${id}/folder-access`,
    inviteGroup: '/user/invite-group',
    roleGroup: '/user/role-group',
    folderAccessUserIds: (folderId: number) =>
      `/user/folder-access/${folderId}`,
    folderAccessState: '/user/folder-access/state',
    folderAccessGroup: '/user/folder-access/group',
    documents: (id: number) => `/user/${id}/documents`,
    documentsPreview: (id: number) => `/user/${id}/documents/preview`,
    documentDownload: (id: number, documentId: number) =>
      `/user/${id}/documents/${documentId}/download`,
  },
  organization: '/organization',
  folder: {
    root: (id?: number) => `/folder/${id ?? ''}`,
    linkFiles: (folderId: number) => `/folder/${folderId}/files/link`,
    removeFile: (folderId: number, fileId: number) =>
      `/folder/${folderId}/files/delete/${fileId}`,
    regenerateAcls: '/folder/regenerate-acls',
  },
  equipment: {
    root: (id?: number) => `/equipments/${id || ''}`,
    list: '/equipments/list',
    lookup: '/equipments/lookup',
    histories: '/equipments/history',
    inspect: {
      root: (id: number) => `/equipments/${id}/inspect`,
      multiple: '/equipments/inspect',
    },
    allocate: (id: number) => `/equipments/${id}/allocate`,
    updateDeadlines: (id: number) => `/equipments/deadlines/update/${id}`,
    bulk: '/equipments/bulk', // bulk operations
    locations: {
      fleetMap: '/equipments/locations',
      root: (id: number) => `/equipments/${id}/locations`,
    },
  },
  model: {
    root: (id?: number) => `/models/${id || ''}`,
    search: '/models/search',
  },
  brand: {
    root: (id?: number) => `/brands/${id || ''}`,
  },
  category: {
    root: '/categories',
  },
  transfer: {
    root: (id?: number) => `/transfer/${id || ''}`,
    validate: (token: string) => `/transfer/public/validate/${token}`,
    check: (externalId: string) => `/transfer/public/check/${externalId}`,
    respond: '/transfer/public/respondToTransfer',
    certificate: (id: number) => `/transfer/${id}/certificate`,
  },
  tag: {
    root: (id?: number) => `/tags/${id || ''}`,
  },
  incident: {
    root: (id?: number) => `/incident/${id || ''}`,
    close: (id: number) => `/incident/${id}/close`,
    reopen: (id: number) => `/incident/${id}/reopen`,
    people: (id: number) => `/incident/${id}/people`,
    person: (id: number, personId: number) =>
      `/incident/${id}/people/${personId}`,
    actions: (id: number) => `/incident/${id}/actions`,
    action: (id: number, actionId: number) =>
      `/incident/${id}/actions/${actionId}`,
    files: (id: number) => `/incident/${id}/files`,
    file: (id: number, documentId: number) =>
      `/incident/${id}/files/${documentId}`,
    fileUrl: (id: number, documentId: number) =>
      `/incident/${id}/files/${documentId}/url`,
    qrCodes: '/incident/qr-codes',
    revokeQrCode: (id: number) => `/incident/qr-codes/${id}/revoke`,
    settings: '/incident/settings',
    // Public poster route — the path is printed on physical signs, never rename it.
    publicContext: (token: string) => `/incident/public/context/${token}`,
    publicDeclare: (token: string) => `/incident/public/${token}`,
  },
  hseSettings: '/hse-settings',
  training: {
    root: (id?: number) => `/training/${id || ''}`,
    user: (userId: number) => `/training/user/${userId}`,
  },
  medicalVisit: {
    root: (id?: number) => `/medical-visits/${id || ''}`,
    user: (userId: number) => `/medical-visits/user/${userId}`,
    userTypes: (userId: number) => `/medical-visits/user/${userId}/types`,
    files: '/medical-visits/files',
    fileDownload: (id: number, fileId: number) =>
      `/medical-visits/${id}/files/${fileId}/download`,
  },
  medicalVisitType: {
    root: (id?: number) => `/medical-visit-types/${id || ''}`,
    config: () => '/medical-visit-types/config',
    unassignedUsersCount: () => '/medical-visit-types/unassigned-users-count',
    assignDefaults: () => '/medical-visit-types/assign-defaults',
  },
  habilitation: {
    root: (id?: number) => `/habilitations/${id || ''}`,
    user: (userId: number) => `/habilitations/user/${userId}`,
    files: '/habilitations/files',
    fileDownload: (id: number, fileId: number) =>
      `/habilitations/${id}/files/${fileId}/download`,
  },
  habilitationType: {
    root: (id?: number) => `/habilitation-types/${id || ''}`,
  },
  orgDocument: {
    root: (id?: number) => `/org-documents/${id ?? ''}`,
    admin: '/org-documents/admin',
    files: '/org-documents/files',
    download: (id: number) => `/org-documents/${id}/download`,
    versions: (id: number) => `/org-documents/${id}/versions`,
    recipients: (id: number) => `/org-documents/${id}/recipients`,
    acknowledge: (id: number) => `/org-documents/${id}/acknowledge`,
    group: (groupId: string) => `/org-documents/group/${groupId}`,
    confirm: (token: string) => `/org-documents/confirm/${token}`,
    confirmDownload: (token: string) =>
      `/org-documents/confirm/${token}/download`,
  },
  files: '/files',
  fileImport: '/files/import',
  images: '/images',
  stats: {
    root: '/stats',
    dashboard: '/stats/dashboard',
  },
  import: {
    root: '/import',
    models: '/import/models',
    equipments: '/import/equipments',
    customModels: '/import/customModels',
  },
  export: {
    root: '/export',
    users: '/export/users',
  },
  billing: {
    info: '/billing/info',
    manageSubscription: '/billing/manageSubscription',
    synchronizeWithStripe: 'billing/synchronizeWithStripe',
  },
  events: {
    root: '/events',
    user: (id: number) => `/events/user/${id}`,
  },
  admin: {
    organizations: '/admin/organizations',
    organization: (id: number) => `/admin/organizations/${id}`,
    studio: '/admin/studio',
    users: '/admin/users',
    user: (id: number) => `/admin/users/${id}`,
    impersonate: (id: number) => `/admin/users/${id}/impersonate`,
    mails: '/admin/mails',
    brands: '/admin/brands',
    brandApprove: (id: number) => `/admin/brands/${id}/approve`,
    brandReject: (id: number) => `/admin/brands/${id}/reject`,
    actions: {
      recalculateUserCompliance: '/admin/actions/recalculate-user-compliance',
      recalculateTrainingStatuses:
        '/admin/actions/recalculate-training-statuses',
      reindexModels: '/admin/actions/reindex-models',
      checkEquipmentStatusChanges:
        '/admin/actions/check-equipment-status-changes',
      job: (id: string) => `/admin/actions/jobs/${id}`,
    },
    userImport: {
      template: '/admin/user-import/template',
      analyze: '/admin/user-import/analyze',
      execute: '/admin/user-import/execute',
      job: (id: string) => `/admin/user-import/jobs/${id}`,
    },
    billing: {
      clients: '/admin/billing/clients',
      client: (id: number) => `/admin/billing/clients/${id}`,
      clientInvoices: (id: number) => `/admin/billing/clients/${id}/invoices`,
      invoiceVoid: (id: number) => `/admin/billing/invoices/${id}/void`,
      invoiceResend: (id: number) => `/admin/billing/invoices/${id}/resend`,
    },
  },
  distributor: {
    dashboard: '/distributor/dashboard',
    organizations: '/distributor/organizations',
    organization: (publicId: string) =>
      `/distributor/organizations/${publicId}`,
    equipments: '/distributor/equipments',
    referencesAnalytics: '/distributor/references/analytics',
    users: '/distributor/users',
    user: (publicId: string) => `/distributor/users/${publicId}`,
    impersonate: (publicId: string) =>
      `/distributor/users/${publicId}/impersonate`,
  },
  allowances: '/allowances',
  inspectionChecklist: {
    root: (id?: number) => `/inspection-checklists/${id ?? ''}`,
  },
  inspection: {
    root: (id?: number) => `/inspection/${id ?? ''}`,
  },
  repair: {
    root: (id?: number) => `/repair/${id ?? ''}`,
  },
  shortlink: {
    public: (id: string) => `/shortlink/public/${id}`,
    private: (id: string) => `/shortlink/private/${id}`,
  },
  agent: {
    // Streamed with `fetch`, not axios: see `sections/agent/agent-stream.ts`.
    chat: '/agent/chat',
    decision: (publicId: string, toolCallId: string) =>
      `/agent/conversations/${publicId}/tool-calls/${toolCallId}/decision`,
    conversations: '/agent/conversations',
    conversation: (publicId: string) => `/agent/conversations/${publicId}`,
    usage: '/agent/usage',
  },
};
